import React from 'react';
import ReactECharts from 'echarts-for-react';

const CCEMedianDesignChart = ({ designCO2eqMedian, analysisPeriod, variant, title, max }) => {
  if (!designCO2eqMedian || designCO2eqMedian.length === 0) {
    return <div>Loading...</div>; // Or any other loading indicator
  }

  // Calculate the data for the chart
  const CO2eq1Total = designCO2eqMedian.map(period => period.reduce((acc, val) => acc + val, 0) / 1e6);
  const CO2eq1Embodied = designCO2eqMedian.map(period => (period[0] + period[1]) / 1e6);

  const X = Array.from({ length: analysisPeriod }, (_, i) => i + 1);

  // Define styles based on the variant
  const styles = {
    1: {
      totalLineColor: '#0072B2', // Blue color for the total line
      embodiedFillColor: '#808080', // Gray color for the embodied area
          useFillColor: '#abd7db', // Green color for the use area
    },
    2: {
      totalLineColor: '#FF5733', // Different color for variant 2
      embodiedFillColor: '#808080',
      useFillColor: '#d2b16e',
    },
  };

  // Select the styles based on the variant
  const selectedStyle = styles[variant] || styles[1]; // Default to variant 1 if none is provided

  // ECharts option configuration
  const option = {
    title: {
      text: `Life Cycle GHG Emission: ${title || ''}`,
      left: 'center',
      fontSize: 14,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      formatter: function (params) {
        const year = params[0]?.name || '';
        const totalValue = params.find(p => p.seriesName === 'Total')?.data || 0;
        const useValue = params.find(p => p.seriesName === 'Use')?.data || 0;

        let tooltipContent = `Year: ${year}<br/>`;
        if (totalValue !== undefined) {
          tooltipContent += `Total: ${totalValue.toFixed(3)} kt<br/>`;
        }
        if (useValue !== undefined) {
          tooltipContent += `Use: ${useValue.toFixed(3)} kt<br/>`;
        }
        return tooltipContent;
      },
    },
    xAxis: {
      type: 'category',
      data: X,
      name: 'Analysis Period (Years)',
      nameLocation: 'middle',
      nameGap: 30,
          // Custom ticks to display
          axisTick: {
              show: false,
              interval: 5, // Ensure all ticks are shown
          },
          min: 0, // Set minimum value for x-axis
          max: 50, // Set maximum value for x-axis
          // Set the tick values explicitly
          axisLabel: {
              formatter: function (value) {
                  // Create custom ticks: 1, 5, 10, 15, ..., 50
                  const customTicks = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
                  return customTicks.includes(parseInt(value)) ? value : '';
              },
              interval: 0 // This will ensure every label is checked for formatting
          },
      },
    yAxis: {
        type: 'value',
        axisLine: {
            show: true, // Show the axis line with default color
        },
        axisTick: {
            show: true, // Show tick marks with default style
        },
      name: 'Carbon Emission (kt of CO2eq)',
      nameLocation: 'middle',
      nameGap: 50,
      min: 0,
      max: max,
      axisLabel: {
        fontSize: 14,
        formatter: '{value} ',
      },
    },
    legend: {
      data: [
        { name: 'Total', icon: 'circle', itemStyle: { color: selectedStyle.totalLineColor } },
        { name: 'Use', icon: 'circle', itemStyle: { color: selectedStyle.useFillColor } },
        { name: 'Embodied', icon: 'circle', itemStyle: { color: selectedStyle.embodiedFillColor } },
      ],
      top: '10%',
    },
    series: [
      {
        name: 'Total',
        type: 'line',
        data: CO2eq1Total,
        lineStyle: {
          color: selectedStyle.totalLineColor,
          width: 1.5,
        },
        showSymbol: false,
      },
      {
        name: 'Embodied',
        type: 'line',
        data: CO2eq1Embodied,
        lineStyle: {
          color: selectedStyle.embodiedFillColor, // Ensure the line color matches the legend
          opacity: 0, // Keep the line itself invisible
        },
        areaStyle: {
          color: selectedStyle.embodiedFillColor, // Ensure the area color matches the legend
        },
        stack: 'confidence-band',
        symbol: 'none',
      },
      {
        name: 'Use',
        type: 'line',
        data: CO2eq1Total.map((total, idx) => total - CO2eq1Embodied[idx]), // Difference between Total and Embodied
        lineStyle: {
          color: selectedStyle.useFillColor, // Ensure the line color matches the legend
          opacity: 0, // Keep the line itself invisible
        },
        areaStyle: {
          color: selectedStyle.useFillColor, // Ensure the area color matches the legend
        },
        stack: 'confidence-band',
        symbol: 'none',
      },
    ],
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
      containLabel: true,
    },
  };

  return <ReactECharts option={option} style={{ height: '500px', width: '600px' }} />;
};

export default CCEMedianDesignChart;
